/**
 * sky_look — what stands in a system's sky beyond the system (the natural object layer, 2026-10-07). PURE.
 *
 * Three things, all far outside the lanes and none of them a place:
 *  - a STAR CLUSTER (catalog SC-01…05). What tells the kinds apart is not a label but a population (Britannica "star
 *    cluster"; PSU Astro 801 L7): a GLOBULAR is 10⁴–10⁶ old stars in a round, centrally concentrated ball — no blue main
 *    sequence left, its brightest stars are RED GIANTS, with a few blue horizontal-branch stars, over an unresolved glow;
 *    an OPEN cluster is a few dozen to a few hundred stars in an irregular loose group — young ones dominated by a
 *    handful of blue stars (the Pleiades: six to the eye, ~2° wide), older ones yellow-white with a few orange giants
 *    (the Hyades); a YOUNG MASSIVE cluster is a compact blaze of hot blue stars with a few red supergiants (R136,
 *    Westerlund 1); an EMBEDDED cluster is still inside its cloud — a few bright stars at the centre (the Trapezium) and
 *    the rest dimmed and REDDENED by dust; an ASSOCIATION is not a cluster at all but a loose stream of blue stars
 *    across many degrees (Sco-Cen, Orion OB1) with a red supergiant or two.
 *  - a DISTANT COMPANION SUN (a wide binary's other star, a triple's third): a small disc with a glow, a second sun
 *    that lights nothing.
 *  - a GUEST STAR: a far transient on the wall clock, the same in every sky (catalog EV-03…07). The light curves keep
 *    their real SHAPES with days compressed to minutes: a Type Ia rises ~18 d and declines over weeks; a Type II-P
 *    holds a ~100 d plateau several times fainter and then drops; a kilonova peaks within a day and reddens from
 *    ~6,400 K to ~3,500 K in three (AT 2017gfo, half-life ~40 h); a gamma-ray burst's afterglow peaks in seconds and
 *    falls as a power law (GRB 080319B was a naked-eye star for half a minute); a red nova is slow, red and double-humped.
 */
import { kelvinToRgb } from '../space_look.js';
import type { SkyClusterKind } from './natural_types.js';

export type Rgb = [number, number, number];

/** A small deterministic generator (mulberry32) — sequences, never keys that differ in a last character. */
export function rng(seed: number): () => number {
  let a = (seed >>> 0) || 0x9e3779b9;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const srgbToLin = (c: number): number => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
/** A blackbody's colour as LINEAR rgb, max channel 1. */
export function starRgb(kelvin: number): Rgb {
  const s = kelvinToRgb(kelvin);
  const l: Rgb = [srgbToLin(s[0]), srgbToLin(s[1]), srgbToLin(s[2])];
  const m = Math.max(l[0], l[1], l[2], 1e-6);
  return [l[0] / m, l[1] / m, l[2] / m];
}

// ── clusters ────────────────────────────────────────────────────────────────────────────────────────────────────
/** Floats per star: offset x, y, z (in cluster radii; z along the line of sight), r, g, b (linear × flux), core radius (px at 1080p). */
export const STAR_STRIDE = 7;
export interface ClusterStars {
  kind: SkyClusterKind;
  count: number;
  /** `count × STAR_STRIDE`, brightest first. */
  data: Float32Array;
  /** The unresolved light: a soft disc (radius in cluster radii, linear rgb × flux), or null. */
  glow: { radius: number; rgb: Rgb } | null;
}
/** Stars per kind at the high level: [min, max]. */
export const CLUSTER_COUNT: Readonly<Record<SkyClusterKind, readonly [number, number]>> = {
  globular_cluster: [900, 1400], open_cluster: [70, 150], young_cluster: [260, 380], embedded_cluster: [45, 80], association: [26, 44],
};
/** The brightest star's flux (the sky's own star cores reach ~6.8). */
export const CLUSTER_PEAK: Readonly<Record<SkyClusterKind, number>> = { globular_cluster: 1.6, open_cluster: 5.5, young_cluster: 3.2, embedded_cluster: 5, association: 7 };
const LEVEL_SCALE = { low: 0.5, high: 1, ultra: 1.5 } as const;

/** A Plummer sphere's radius for a uniform deviate (r = a / √(u^(−2/3) − 1)), cut at `cut` scale radii. */
export function plummerR(u: number, cut = 5): number {
  const uu = Math.min(0.999, Math.max(1e-6, u));
  return Math.min(cut, 1 / Math.sqrt(Math.pow(uu, -2 / 3) - 1));
}

/** The stars of a cluster: positions in units of its radius, colours and fluxes by its population. Deterministic in (kind, seed, level). */
export function clusterStars(kind: SkyClusterKind, seed: number, level: 'low' | 'high' | 'ultra' = 'high'): ClusterStars {
  const r = rng(seed * 2654435761 + kind.length * 97);
  const [lo, hi] = CLUSTER_COUNT[kind];
  const n = Math.max(12, Math.round((lo + r() * (hi - lo)) * LEVEL_SCALE[level]));
  const peak = CLUSTER_PEAK[kind];
  const out: Array<{ x: number; y: number; z: number; rgb: Rgb; flux: number; px: number }> = [];
  const dir = (): [number, number, number] => { const z = r() * 2 - 1, t = r() * Math.PI * 2, s = Math.sqrt(1 - z * z); return [s * Math.cos(t), s * Math.sin(t), z]; };
  // a seeded stretch axis: an open cluster or an association is never round
  const ax = r() * Math.PI, stretch = kind === 'association' ? 2.0 + r() * 0.8 : kind === 'open_cluster' ? 1 + r() * 0.5 : kind === 'embedded_cluster' ? 1 + r() * 0.3 : 1;
  const ca = Math.cos(ax), sa = Math.sin(ax);
  // sub-clumps (an open cluster's knots)
  const clumps: Array<[number, number, number]> = [];
  if (kind === 'open_cluster' || kind === 'embedded_cluster') for (let c = 0; c < 2 + Math.floor(r() * 2); c++) { const d = dir(); const rr = 0.25 + r() * 0.4; clumps.push([d[0] * rr, d[1] * rr, d[2] * rr]); }
  const old = kind === 'open_cluster' && r() < 0.4; // a Hyades, not a Pleiades
  let glowK = 5200;
  for (let i = 0; i < n; i++) {
    // where
    let x: number, y: number, z: number;
    if (kind === 'association') {
      // no core: a filled ellipse, slightly knotted
      const d = dir(), rr = Math.cbrt(r()) * 0.95;
      x = d[0] * rr; y = d[1] * rr; z = d[2] * rr;
    } else {
      const a = kind === 'globular_cluster' ? 0.2 : kind === 'young_cluster' ? 0.17 : 0.34; // Plummer scale radius in cluster radii
      const d = dir(), rr = plummerR(r(), kind === 'globular_cluster' ? 6.5 : 3.2) * a;
      x = d[0] * rr; y = d[1] * rr; z = d[2] * rr;
      if (clumps.length && r() < 0.45) { const c = clumps[Math.floor(r() * clumps.length)]; x = c[0] + x * 0.45; y = c[1] + y * 0.45; z = c[2] + z * 0.45; }
    }
    const sx = x * ca - y * sa, sy = x * sa + y * ca;
    x = sx * stretch; y = sy / Math.sqrt(stretch);
    // what
    const u = r(), v = r();
    let kelvin: number, bright: number; // bright ∈ (0, 1]: the star's place in the luminosity function
    const faint = Math.pow(v, 3.2); // most stars are faint
    if (kind === 'globular_cluster') {
      // an old population: the brightest are red giants; a few blue horizontal-branch stars; the rest turn-off yellow-white
      if (u < 0.11) { kelvin = 3900 + r() * 700; bright = 0.45 + 0.55 * Math.pow(v, 1.5); }
      else if (u < 0.19) { kelvin = 8000 + r() * 3500; bright = 0.22 + 0.18 * v; }
      else { kelvin = 5200 + r() * 1100; bright = 0.05 + 0.3 * faint; }
    } else if (kind === 'young_cluster') {
      if (u < 0.05) { kelvin = 3500 + r() * 2800; bright = 0.5 + 0.5 * v; } // red supergiants, yellow hypergiants
      else { kelvin = 14000 + r() * 24000; bright = 0.1 + 0.9 * faint; }
      glowK = 16000;
    } else if (kind === 'association') {
      if (i < 2) { kelvin = 3500 + r() * 400; bright = 0.7 + 0.3 * v; } // its red supergiants
      else { kelvin = 13000 + r() * 16000; bright = 0.22 + 0.78 * Math.pow(v, 2); }
    } else if (kind === 'embedded_cluster') {
      kelvin = i < 4 ? 26000 + r() * 12000 : 3800 + Math.pow(r(), 2) * 16000;
      bright = i < 4 ? 0.7 + 0.3 * v : 0.08 + 0.5 * faint;
      if (i < 4) { x *= 0.12; y *= 0.12; z *= 0.12; } // the trapezium at the heart
    } else if (old) {
      if (u < 0.05) { kelvin = 4300 + r() * 500; bright = 0.6 + 0.4 * v; } // orange giants (the Hyades' four brightest)
      else { kelvin = 5600 + r() * 2400; bright = 0.08 + 0.6 * faint; }
    } else {
      kelvin = u < 0.12 ? 11000 + r() * 9000 : 5500 + Math.pow(r(), 1.6) * 6500;
      bright = u < 0.12 ? 0.5 + 0.5 * v : 0.06 + 0.4 * faint;
    }
    const rgb = starRgb(kelvin);
    let flux = peak * bright;
    if (kind === 'embedded_cluster' && i >= 4) {
      // dust between us and the star: dimmer, and redder (blue is absorbed ~1.6× the red)
      const av = Math.pow(r(), 0.7) * 3.2, k = Math.pow(10, -0.4 * av);
      rgb[0] *= Math.pow(k, 0.75); rgb[1] *= k; rgb[2] *= Math.pow(k, 1.3);
      const m = Math.max(rgb[0], rgb[1], rgb[2], 1e-6); flux *= m; rgb[0] /= m; rgb[1] /= m; rgb[2] /= m;
    }
    out.push({ x, y, z, rgb, flux, px: 0.9 + 1.5 * Math.sqrt(Math.min(1, flux / 6)) });
  }
  out.sort((p, q) => q.flux - p.flux);
  const data = new Float32Array(out.length * STAR_STRIDE);
  out.forEach((s, i) => { data.set([s.x, s.y, s.z, s.rgb[0] * s.flux, s.rgb[1] * s.flux, s.rgb[2] * s.flux, s.px], i * STAR_STRIDE); });
  const glowRgb = starRgb(glowK);
  const glow = kind === 'globular_cluster' ? { radius: 0.55, rgb: [glowRgb[0] * 0.2, glowRgb[1] * 0.2, glowRgb[2] * 0.2] as Rgb }
    : kind === 'young_cluster' ? { radius: 0.5, rgb: [glowRgb[0] * 0.14, glowRgb[1] * 0.14, glowRgb[2] * 0.14] as Rgb }
    : kind === 'open_cluster' && !old ? { radius: 0.9, rgb: [0.012, 0.02, 0.04] as Rgb } // the faint blue of dust the cluster is passing through
    : null;
  return { kind, count: out.length, data, glow };
}

// ── a distant companion sun ─────────────────────────────────────────────────────────────────────────────────────
/** Its drawn radius (m): a companion is a main-sequence star, smaller the cooler. */
export function distantSunRadiusM(kind: string): number {
  return kind === 'm_dwarf' ? 42_000 : kind === 'k_dwarf' ? 58_000 : kind === 'g_dwarf' ? 76_000 : kind === 'f_dwarf' ? 88_000 : 70_000;
}

// ── guest stars ─────────────────────────────────────────────────────────────────────────────────────────────────
export type TransientKind = 'sn_ia' | 'sn_core' | 'kilonova' | 'grb' | 'red_nova';
/** One slot of the wall clock holds at most one guest star. */
export const TRANSIENT_SLOT_S = 2400;
/** Share of slots with a guest star, and the kinds' shares among those. */
export const TRANSIENT_SHARE = 0.45;
const TRANSIENT_KINDS: ReadonlyArray<readonly [TransientKind, number]> = [['sn_core', 0.36], ['sn_ia', 0.3], ['red_nova', 0.14], ['kilonova', 0.11], ['grb', 0.09]];
/** How long each is seen (s): its whole light curve fits its slot. */
export const TRANSIENT_LIFE_S: Readonly<Record<TransientKind, number>> = { sn_ia: 1500, sn_core: 1800, kilonova: 420, grb: 360, red_nova: 1300 };
/** Peak flux (the sky's star cores reach ~6.8: a supernova outshines every star in the sky; the eye's range is compressed). */
export const TRANSIENT_PEAK: Readonly<Record<TransientKind, number>> = { sn_ia: 46, sn_core: 20, kilonova: 5, grb: 70, red_nova: 9 };
export const TRANSIENT_LABEL: Readonly<Record<TransientKind, string>> = {
  sn_ia: 'a white-dwarf supernova', sn_core: 'a collapsing giant\'s supernova', kilonova: 'a kilonova — two neutron stars merging', grb: 'a gamma-ray burst\'s afterglow', red_nova: 'a red nova — two stars merging',
};

function mix32(a: number): number { let h = a >>> 0; h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16; return h >>> 0; }
export interface Transient { kind: TransientKind; slot: number; ageS: number; dir: [number, number, number]; startS: number }
/** The guest star of the slot that holds wall-clock second `nowS` (null = none, or its light is over). */
export function transientAt(nowS: number): Transient | null {
  if (!Number.isFinite(nowS)) return null;
  const slot = Math.floor(nowS / TRANSIENT_SLOT_S);
  const h = (k: number): number => mix32(mix32(slot + 0x51ed27 * k) ^ 0x9e3779b9) / 4294967296;
  if (h(1) >= TRANSIENT_SHARE) return null;
  let t = h(2), kind: TransientKind = 'sn_core';
  for (const [k, w] of TRANSIENT_KINDS) { if ((t -= w) < 0) { kind = k; break; } }
  // it begins a little way into its slot, and ends before the slot does
  const startS = slot * TRANSIENT_SLOT_S + 30 + h(3) * Math.max(0, TRANSIENT_SLOT_S - TRANSIENT_LIFE_S[kind] - 60);
  const ageS = nowS - startS;
  if (ageS < 0 || ageS > TRANSIENT_LIFE_S[kind]) return null;
  const z = h(4) * 2 - 1, a = h(5) * Math.PI * 2, s = Math.sqrt(1 - z * z);
  return { kind, slot, ageS, dir: [s * Math.cos(a), z, s * Math.sin(a)], startS };
}
const sstep = (a: number, b: number, x: number): number => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
/** A guest star's light at `ageS` seconds: flux (0 outside its life) and its colour (linear, max channel 1). */
export function transientLight(kind: TransientKind, ageS: number): { flux: number; rgb: Rgb } {
  const life = TRANSIENT_LIFE_S[kind], peak = TRANSIENT_PEAK[kind];
  if (!(ageS >= 0 && ageS <= life)) return { flux: 0, rgb: [1, 1, 1] };
  const end = 1 - sstep(life * 0.9, life, ageS); // every curve leaves the sky without a pop
  let f: number, kelvin: number;
  if (kind === 'sn_ia') {
    // ~18 d to maximum, ~0.1 mag/day for a month, then the slow radioactive tail; white, reddening
    const rise = 130, t = ageS - rise;
    f = t < 0 ? Math.pow(ageS / rise, 2) : 0.82 * Math.exp(-t / 260) + 0.18 * Math.exp(-t / 1200);
    kelvin = t < 0 ? 11000 : 11000 - 6200 * sstep(0, 700, t);
  } else if (kind === 'sn_core') {
    // a fast rise, a long plateau as the hydrogen envelope recombines, then the drop off its edge onto a faint tail
    const rise = 45, plateau = 1150;
    f = ageS < rise ? Math.pow(ageS / rise, 1.5) : ageS < plateau ? 1 - 0.3 * ((ageS - rise) / (plateau - rise)) : 0.7 * (0.12 + 0.88 * Math.exp(-(ageS - plateau) / 70)) * Math.exp(-(ageS - plateau) / 1500);
    kelvin = 14000 - 8500 * sstep(0, plateau, ageS) - 1500 * sstep(plateau, life, ageS); // the plateau ends at hydrogen's recombination, ~5,500 K; the tail is redder
  } else if (kind === 'kilonova') {
    // peaks within a day and fades with a ~40 h half-life, blue to deep red in three days
    const rise = 22;
    f = ageS < rise ? ageS / rise : Math.pow(0.5, (ageS - rise) / 75);
    kelvin = 6400 - 2900 * sstep(rise, 200, ageS);
  } else if (kind === 'grb') {
    // the prompt flash, then the afterglow's power law
    const rise = 3.5;
    f = ageS < rise ? Math.pow(ageS / rise, 2) : Math.pow(1 + (ageS - rise) / 6, -1.25);
    kelvin = 12000 - 4500 * sstep(20, 300, ageS);
  } else {
    // slow and double-humped, yellow at first and then very red and cool (V838 Mon ended as an L-type supergiant; V1309 Sco)
    const p1 = 240, p2 = 620;
    f = Math.max(Math.exp(-Math.pow((ageS - p1) / 150, 2)), 0.8 * Math.exp(-Math.pow((ageS - p2) / 260, 2))) * sstep(0, 80, ageS);
    kelvin = 5200 - 2700 * sstep(150, 700, ageS);
  }
  return { flux: Math.max(0, f) * peak * end, rgb: starRgb(kelvin) };
}
